import { TeamGoalDto } from "@coaching-culture/types";
import {
  Box,
  Button,
  EmptyState,
  Flex,
  FormInput,
  FormTextArea,
  Modal,
  Text,
} from "@coaching-culture/ui";
import { Toggle } from "components/Toggle";
import UserProfileImage from "components/UserProfileImage";
import { useGoalTerminology } from "auth/OrgProvider";
import { useTeam } from "queries/performance";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaCheck, FaUsers } from "react-icons/fa";
import { useTheme } from "styled-components";

export type EditTeamGoalModalProps = {
  goal?: TeamGoalDto | null;
  onSave: (goal: TeamGoalDto) => void;
  onCancel: () => void;
};

export function EditTeamGoalModal({
  goal,
  onSave,
  onCancel,
}: EditTeamGoalModalProps) {
  const { handleSubmit, reset, errors, register } = useForm<TeamGoalDto>();
  const terms = useGoalTerminology();
  const theme = useTheme();
  const team = useTeam();

  const [appliesToAll, setAppliesToAll] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (goal != null) {
      reset(goal);
      const members = goal.memberIds ?? [];
      setSelectedIds(members);
      setAppliesToAll(members.length === 0);
    } else {
      setSelectedIds([]);
      setAppliesToAll(true);
    }
  }, [goal, reset]);

  const directReports = (team.data?.directReports ?? []).filter(
    (x) =>
      x.relationship.toAccepted != null && x.relationship.fromAccepted != null,
  );

  const toggleMember = (id: string) => {
    setSelectedIds((old) =>
      old.includes(id) ? old.filter((x) => x !== id) : old.concat(id),
    );
  };

  const onSubmit = (values: TeamGoalDto) => {
    onSave({
      ...values,
      id: goal == null ? undefined : goal.id,
      memberIds: appliesToAll ? [] : selectedIds,
    });
  };

  return (
    <Modal width={600} showCloseButton onClose={onCancel} p={5}>
      <Text fontSize={5} fontWeight={600} mb={4}>
        {goal == null
          ? `Create Team ${terms.goal.asTitle()}`
          : `Edit Team ${terms.goal.asTitle()}`}
      </Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          ref={register({ required: true })}
          name="name"
          label="Name"
          error={errors.name}
          required
        />
        <FormTextArea
          ref={register}
          name="description"
          label="Description"
          mb={4}
        />
        <Text fontWeight={600} mb={2}>
          Who does this {terms.goal.asTitle()} apply to?
        </Text>
        <Toggle
          leftLabel="All my team"
          rightLabel="Specific people"
          value={!appliesToAll}
          onChange={(val) => setAppliesToAll(!val)}
          mb={3}
        />
        {!appliesToAll &&
          (directReports.length === 0 ? (
            <EmptyState compact icon={FaUsers} mb={4}>
              You have no direct reports to assign.
            </EmptyState>
          ) : (
            <Box
              mb={4}
              border="1px solid"
              borderColor="border"
              style={{
                maxHeight: 240,
                overflowY: "auto",
                borderRadius: 4,
              }}
            >
              {directReports.map((x) => {
                const active = selectedIds.includes(x.id);
                return (
                  <Flex
                    key={x.id}
                    alignItems="center"
                    justifyContent="space-between"
                    p={2}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleMember(x.id)}
                  >
                    <Flex alignItems="center">
                      <UserProfileImage name={x.name} size="small" mr={2} />
                      <Text
                        fontWeight={500}
                        color={active ? "positive" : "body"}
                      >
                        {x.name}
                      </Text>
                    </Flex>
                    {active && <FaCheck color={theme.colors.positiveText} />}
                  </Flex>
                );
              })}
            </Box>
          ))}
        <Flex>
          <Button type="submit" color="primary">
            Save
          </Button>
          <Button ml={2} type="button" onClick={onCancel}>
            Cancel
          </Button>
        </Flex>
      </form>
    </Modal>
  );
}
